import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import AgentMarkdown from '@/components/agent/AgentMarkdown';
import WorkspacePanel from '@/components/analyst/WorkspacePanel';
import { fetchProjectStatus } from '@/api/analyst';
import useAnalystStore, { analystActions } from '@/stores/analystStore';
import useChatHistoryStore, { chatHistoryActions } from '@/stores/chatHistoryStore';
import PageTitle from '@/components/core/PageTitle';
import useSettingsStore from '@/stores/settingsStore';
import { toast } from '@/utils/toast';
import BetaBadge from '@/components/agent/BetaBadge';

// ---------------------------------------------------------------------------
// Progress indicator — single phase label shown while the agent is running
// ---------------------------------------------------------------------------

const PHASE_LABELS = {
  // Pathway construction
  build_pathway: 'Building pathways',
  search_node_types: 'Building pathways',
  create_pathway_from_template: 'Building pathways',
  copy_case: 'Building pathways',
  // Configuration (runs after pathway creation, before assumptions review)
  set_node_params: 'Configuring',
  set_pathway_params: 'Configuring',
  set_anchor: 'Configuring',
  find_param: 'Configuring',
  enable_location_scaling: 'Configuring',
  get_node_params: 'Configuring',
  get_pathway_summary: 'Configuring',
  list_pathways: 'Configuring',
  // Assumptions review (agent reads what it built before proposing to the user)
  get_study_assumptions: 'Reviewing assumptions',
  propose_analysis: 'Summarizing study plan',
  // Analysis
  balance_case: 'Balancing',
  analyze_case: 'Running analysis',
  // Results
  get_lca_results: 'Reading results',
  get_tea_results: 'Reading results',
  get_comparison_results: 'Reading results',
  get_sensitivity_results: 'Reading results',
  run_sensitivity_sweep: 'Running sweep',
  run_sensitivity_analysis: 'Running sensitivity',
  create_comparison: 'Reading results',
  generate_report: 'Generating report',
  create_workspace_content: 'Building content',
  // Web search
  web_search: 'Searching web',
  // Documentation
  search_docs: 'Searching docs',
  // System (BTM asset / future hydrogen / power dispatch) modeling
  load_skill: 'Loading guidance',
  list_systems: 'Reviewing systems',
  list_system_templates: 'Listing system templates',
  create_system_from_template: 'Creating system',
  create_systems_batch: 'Creating systems',
  get_system_params: 'Reading system',
  get_generator_params: 'Reading generator',
  set_system_params: 'Configuring system',
  set_solar_generator: 'Configuring solar',
  set_wind_generator: 'Configuring wind',
  set_storage_generator: 'Configuring storage',
  set_thermal_generator: 'Configuring thermal',
  set_pathway_powerplant: 'Linking pathway powerplant',
  delete_generator: 'Removing generator',
  propose_system_runs: 'Summarizing system runs',
  get_system_status: 'Checking system status',
  run_system: 'Running optimization',
  get_system_results: 'Reading system results',
  compare_systems: 'Comparing systems',
};

function PhaseIndicator({ pendingActions }) {
  // Use the last action in the array (regardless of status) for a stable label
  // that doesn't flash back to "Preparing study" between tool rounds.
  const lastAction = pendingActions[pendingActions.length - 1];
  const label = lastAction ? (PHASE_LABELS[lastAction.actionType] ?? 'Working') : 'Preparing study';
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="loading loading-spinner loading-xs text-slate-300" />
      <span className="text-xs text-slate-400">{label}</span>
    </div>
  );
}

PhaseIndicator.propTypes = {
  pendingActions: PropTypes.array.isRequired,
};

// ---------------------------------------------------------------------------
// Inline result line — rendered in chat when a result action completes
// ---------------------------------------------------------------------------

function InlineResult({ actionType, data, pathwayName }) {
  if (data?.error) return null;

  const row = content => <div className="py-1.5 pl-3 border-l-2 border-stone-200 my-1 space-y-0.5">{content}</div>;
  const label = txt => (
    <div className="text-[10px] text-slate-400 uppercase tracking-wide flex items-baseline gap-1.5">
      <span>{txt}</span>
      {pathwayName && <span className="normal-case font-normal">· {pathwayName}</span>}
    </div>
  );
  const hero = (value, unit) => (
    <div>
      <span className="text-sm font-semibold text-slate-800 tabular-nums">{value}</span>
      {unit && <span className="text-xs text-slate-400 ml-1.5">{unit}</span>}
    </div>
  );

  if (actionType === 'get_lca_results') {
    return row(
      <>
        {label('Carbon Intensity')}
        {hero(data.total?.toFixed(2), data.unit)}
      </>,
    );
  }
  if (actionType === 'get_tea_results') {
    return row(
      <>
        {label('Levelized Cost')}
        {hero(data.total?.toFixed(2), data.unit)}
      </>,
    );
  }
  if (actionType === 'get_comparison_results') {
    const cases = (data.cases ?? []).filter(c => c.tea_total != null || c.lca_total != null);
    if (!cases.length) return null;
    const unit = cases[0]?.tea_unit ?? cases[0]?.lca_unit ?? '';
    return row(
      <>
        {label('Comparison')}
        <div className="space-y-0.5">
          {cases.map((c, i) => (
            <div key={i} className="flex items-baseline gap-2 text-xs">
              <span className="text-slate-500 truncate">{c.name || c.pathway_name}</span>
              <span className="font-semibold text-slate-800 tabular-nums shrink-0">
                {(c.tea_total ?? c.lca_total)?.toFixed(2)}
                <span className="text-slate-400 font-normal ml-1">{unit}</span>
              </span>
            </div>
          ))}
        </div>
      </>,
    );
  }
  if (actionType === 'get_sensitivity_results') {
    const topTEA = data.tea?.sensitivities?.[0]?.param_label;
    const topLCA = data.lca?.sensitivities?.[0]?.param_label;
    if (!topTEA && !topLCA) return null;
    return row(
      <>
        {label('Top Drivers')}
        <div className="text-xs text-slate-600 space-y-0.5">
          {topTEA && (
            <div>
              Cost: <span className="text-slate-800">{topTEA}</span>
            </div>
          )}
          {topLCA && (
            <div>
              Emissions: <span className="text-slate-800">{topLCA}</span>
            </div>
          )}
        </div>
      </>,
    );
  }
  if (actionType === 'run_sensitivity_sweep') {
    const pts = data.points?.length ?? 0;
    if (!pts) return null;
    return row(
      <>
        {label('Sweep')}
        {hero(`${pts} points`, data.analysis_type?.toUpperCase())}
      </>,
    );
  }
  return null;
}

InlineResult.propTypes = {
  actionType: PropTypes.string.isRequired,
  data: PropTypes.object,
  pathwayName: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

function MessageBubble({ message, onNavigate }) {
  if (message.role === 'result') {
    return <InlineResult actionType={message.actionType} data={message.data} pathwayName={message.pathwayName} />;
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-slate-800 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%] text-sm leading-relaxed">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="py-0.5 text-sm leading-relaxed text-slate-800">
      <AgentMarkdown content={message.content} onNavigate={onNavigate} openLinksInNewTab />
    </div>
  );
}

MessageBubble.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
    actionType: PropTypes.string,
    data: PropTypes.object,
    pathwayName: PropTypes.string,
    summary: PropTypes.string,
  }).isRequired,
  onNavigate: PropTypes.func,
};

// ---------------------------------------------------------------------------
// System notice — non-AI messages from the harness (e.g. project replaced)
// ---------------------------------------------------------------------------

function SystemNotice({ content }) {
  return (
    <div className="flex justify-center py-1">
      <div className="text-xs text-slate-500 italic bg-stone-100 border border-stone-200 rounded-full px-3 py-1.5 max-w-[90%] text-center leading-snug">
        {content}
      </div>
    </div>
  );
}

SystemNotice.propTypes = {
  content: PropTypes.string.isRequired,
};

// ---------------------------------------------------------------------------
// Stale-project banner — shown on entry when persisted projectId is dead
// ---------------------------------------------------------------------------

function StaleProjectBanner({ previousProjectName, onContinue, onStartFresh }) {
  return (
    <div className="shrink-0 mx-3 mb-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
      <p className="text-sm text-slate-700 leading-snug">
        {previousProjectName ? (
          <>
            Your previous project <strong className="font-medium">{previousProjectName}</strong> is no longer available.
          </>
        ) : (
          'Your previous project is no longer available.'
        )}
      </p>
      <p className="text-xs text-slate-500 mt-1 leading-snug">
        You can keep the conversation and continue in a new project, or start fresh.
      </p>
      <div className="mt-2.5 flex gap-2">
        <button
          type="button"
          onClick={onContinue}
          className="text-xs px-3 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors"
        >
          Continue in a new project
        </button>
        <button
          type="button"
          onClick={onStartFresh}
          className="text-xs px-3 py-1.5 bg-white text-slate-700 border border-stone-200 rounded-md hover:bg-stone-50 transition-colors"
        >
          Start fresh
        </button>
      </div>
    </div>
  );
}

StaleProjectBanner.propTypes = {
  previousProjectName: PropTypes.string,
  onContinue: PropTypes.func.isRequired,
  onStartFresh: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Chat empty state — example prompts + session history
// ---------------------------------------------------------------------------

const EXAMPLE_PROMPTS = [
  'Compare blue vs green H₂ for a Gulf Coast refinery',
  'What is the cost and carbon intensity of green ammonia using wind power?',
  'How does SAF from HEFA compare to alcohol-to-jet?',
  'Compare NGCC with and without CCS for a 500 MW plant in Pennsylvania',
];

function ChatEmptyState({ onSelectPrompt, onLoadSession, sessions, isLoading }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 text-center px-5 pb-8 select-none">
      <p className="text-lg font-semibold text-slate-800 mb-1">Sesame Analyst</p>
      <p className="text-xs text-slate-400 leading-relaxed mb-6 max-w-xs">
        Ask questions about energy project economics and emissions. The analyst runs rigorous techno-economic and
        lifecycle models and shows you the results.
      </p>
      <div className="space-y-1.5 w-full max-w-sm text-left">
        {EXAMPLE_PROMPTS.map(prompt => (
          <button
            key={prompt}
            onClick={() => onSelectPrompt(prompt)}
            className="w-full text-left bg-white hover:bg-stone-50 border border-stone-200 rounded px-3 py-2 text-xs text-slate-600 transition-colors"
          >
            {prompt}
          </button>
        ))}
      </div>

      {isLoading && <span className="loading loading-spinner loading-xs text-slate-300 mt-8" />}

      {!isLoading && sessions.length > 0 && (
        <div className="mt-8 w-full max-w-sm text-left">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Recent</p>
          <div className="space-y-px">
            {sessions.slice(0, 6).map(session => (
              <div key={session.id} className="flex items-center gap-2 group py-0.5">
                <button
                  className="flex-1 text-left text-xs text-slate-600 hover:text-slate-900 truncate transition-colors"
                  onClick={() => onLoadSession(session.id)}
                >
                  {session.title}
                </button>
                <span className="text-[10px] text-slate-300 shrink-0 tabular-nums">
                  {chatHistoryActions.formatRelativeTime(session.last_active_at)}
                </span>
                <button
                  className="text-slate-200 hover:text-slate-500 transition-colors opacity-0 group-hover:opacity-100 text-xs shrink-0"
                  onClick={async () => {
                    const deleted = await chatHistoryActions.delete(session.id);
                    if (!deleted) toast.error('Could not delete conversation');
                  }}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

ChatEmptyState.propTypes = {
  onSelectPrompt: PropTypes.func.isRequired,
  onLoadSession: PropTypes.func.isRequired,
  sessions: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

// ---------------------------------------------------------------------------
// Debug stats — per-turn inline tag + cumulative footer bar
// ---------------------------------------------------------------------------

const RATE_LIMITS = { inputTPM: 450_000, outputTPM: 90_000, requestsPM: 1_000 };

// Pricing per million tokens ($/MTok) — update when Anthropic changes rates.
// Keys are matched by substring so "claude-opus-4-7" matches "opus".
const MODEL_PRICING = {
  opus: { input: 15.0, output: 75.0, cacheWrite: 18.75, cacheRead: 1.5 },
  sonnet: { input: 3.0, output: 15.0, cacheWrite: 3.75, cacheRead: 0.3 },
  haiku: { input: 0.8, output: 4.0, cacheWrite: 1.0, cacheRead: 0.08 },
};

function modelPricing(model = '') {
  const m = model.toLowerCase();
  if (m.includes('opus')) return MODEL_PRICING.opus;
  if (m.includes('haiku')) return MODEL_PRICING.haiku;
  return MODEL_PRICING.sonnet;
}

function computeCost(t) {
  const p = modelPricing(t.model);
  return (
    ((t.input_tokens ?? 0) * p.input +
      (t.output_tokens ?? 0) * p.output +
      (t.cache_write_tokens ?? 0) * p.cacheWrite +
      (t.cache_read_tokens ?? 0) * p.cacheRead) /
    1_000_000
  );
}

function fmtCost(usd) {
  if (usd < 0.01) return '<$0.01';
  return `$${usd.toFixed(2)}`;
}

function DebugTurnTag({ stats }) {
  const [open, setOpen] = useState(false);
  if (!stats) return null;
  const cost = computeCost(stats);

  return (
    <div className="mt-2 text-xs">
      <button
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-100 text-slate-400 hover:text-slate-600 font-mono"
        onClick={() => setOpen(o => !o)}
      >
        {stats.rounds} rounds · {stats.elapsed_s}s · {stats.input_tokens.toLocaleString()} billed in · cache{' '}
        {stats.cache_hit_pct}% · {fmtCost(cost)}
        <span className="text-[10px]">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="mt-1 border border-slate-200 rounded-lg bg-white px-3 py-2 space-y-1.5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-slate-500">
            <div>
              Model: <span className="text-slate-700">{stats.model ?? '?'}</span>
            </div>
            <div>
              Cost: <span className="text-slate-700 tabular-nums">{fmtCost(cost)}</span>
            </div>
            <div>
              Billed input: <span className="text-slate-700 tabular-nums">{stats.input_tokens.toLocaleString()}</span>
            </div>
            <div>
              Output: <span className="text-slate-700 tabular-nums">{stats.output_tokens.toLocaleString()}</span>
            </div>
            <div>
              Cache read:{' '}
              <span className="text-slate-700 tabular-nums">{stats.cache_read_tokens.toLocaleString()}</span>
            </div>
            <div>
              Cache write:{' '}
              <span className="text-slate-700 tabular-nums">{stats.cache_write_tokens.toLocaleString()}</span>
            </div>
            <div>
              Errors: <span className="text-slate-700 tabular-nums">{stats.total_errors}</span>
            </div>
          </div>
          {stats.round_details?.length > 0 && (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-slate-400">
                  <th className="text-left py-0.5">Rnd</th>
                  <th className="text-left">Tools</th>
                  <th className="text-right">LLM</th>
                  <th className="text-right">Tools</th>
                  <th className="text-right">In</th>
                  <th className="text-right">Out</th>
                </tr>
              </thead>
              <tbody>
                {stats.round_details.map(r => (
                  <tr key={r.round} className="text-slate-500 border-t border-slate-50">
                    <td className="py-0.5 tabular-nums">{r.round}</td>
                    <td className="truncate max-w-[200px]">{r.tools.join(', ') || '—'}</td>
                    <td className="text-right tabular-nums">{r.llm_time_s}s</td>
                    <td className="text-right tabular-nums">{r.tool_time_s}s</td>
                    <td className="text-right tabular-nums">{r.input_tokens}</td>
                    <td className="text-right tabular-nums">{r.output_tokens}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

DebugTurnTag.propTypes = { stats: PropTypes.object };

function ThinkingBubble({ content }) {
  const [open, setOpen] = useState(false);
  if (!content) return null;

  const preview = content.length > 80 ? content.slice(0, 80) + '…' : content;

  return (
    <div className="mt-1 mb-2 text-xs">
      <button
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-violet-50 text-violet-400 hover:text-violet-600 font-mono transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-violet-300">thinking</span>
        {!open && <span className="text-violet-300 truncate max-w-[300px]">{preview}</span>}
        <span className="text-[10px]">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="mt-1 border border-violet-100 rounded-lg bg-violet-50/50 px-3 py-2 text-[11px] text-violet-600 font-mono whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
          {content}
        </div>
      )}
    </div>
  );
}

ThinkingBubble.propTypes = { content: PropTypes.string };

function UsageBar({ label, value, limit }) {
  const pct = Math.min((value / limit) * 100, 100);
  const color = pct > 80 ? 'bg-red-400' : pct > 50 ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-slate-400 shrink-0 w-16 text-right">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="tabular-nums text-slate-500 shrink-0 w-28 text-right">
        {(value / 1000).toFixed(1)}K / {(limit / 1000).toFixed(0)}K
      </span>
    </div>
  );
}

UsageBar.propTypes = { label: PropTypes.string, value: PropTypes.number, limit: PropTypes.number };

function DebugFooter({ history }) {
  const [expanded, setExpanded] = useState(false);
  const [nowSec, setNowSec] = useState(0);

  useEffect(() => {
    if (!expanded) return undefined;

    const updateNow = () => setNowSec(Date.now() / 1000);
    updateNow();
    const intervalId = window.setInterval(updateNow, 1000);

    return () => window.clearInterval(intervalId);
  }, [expanded]);

  if (!history || history.length === 0) return null;

  const totals = history.reduce(
    (acc, t) => ({
      input: acc.input + (t.input_tokens ?? 0),
      output: acc.output + (t.output_tokens ?? 0),
      cacheRead: acc.cacheRead + (t.cache_read_tokens ?? 0),
      rounds: acc.rounds + (t.rounds ?? 0),
      elapsed: acc.elapsed + (t.elapsed_s ?? 0),
      errors: acc.errors + (t.total_errors ?? 0),
      cost: acc.cost + computeCost(t),
    }),
    { input: 0, output: 0, cacheRead: 0, rounds: 0, elapsed: 0, errors: 0, cost: 0 },
  );

  const model = history[history.length - 1]?.model ?? '?';

  return (
    <div className="shrink-0 border-t border-slate-200 bg-slate-50 text-xs select-none">
      <button
        className="w-full flex items-center justify-between px-4 py-1.5 text-slate-400 hover:text-slate-600"
        onClick={() => setExpanded(o => !o)}
      >
        <span className="font-mono">
          {model} · {history.length} turn{history.length > 1 ? 's' : ''} · {totals.input.toLocaleString()} billed in ·{' '}
          {totals.output.toLocaleString()} out · {Math.round(totals.elapsed)}s · {fmtCost(totals.cost)}
        </span>
        <span className="text-[10px]">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 space-y-3 border-t border-slate-100 pt-2">
          {/* Rate limit bars — rolling 60s window vs per-minute limits */}
          {history.length > 0 &&
            (() => {
              const recent = history.filter(t => t.timestamp && nowSec - t.timestamp < 60);
              const hasTimestamps = recent.length > 0;
              const source = hasTimestamps ? recent : [history[history.length - 1]];
              const rInput = source.reduce((s, t) => s + (t.input_tokens ?? 0), 0);
              const rOutput = source.reduce((s, t) => s + (t.output_tokens ?? 0), 0);
              const rRounds = source.reduce((s, t) => s + (t.rounds ?? 0), 0);
              const label = hasTimestamps ? 'Last 60s vs. rate limit (per min)' : 'Last turn vs. rate limit (per min)';
              return (
                <div className="space-y-1.5">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</div>
                  <UsageBar label="Input" value={rInput} limit={RATE_LIMITS.inputTPM} />
                  <UsageBar label="Output" value={rOutput} limit={RATE_LIMITS.outputTPM} />
                  <UsageBar label="API calls" value={rRounds} limit={RATE_LIMITS.requestsPM} />
                </div>
              );
            })()}

          {/* Per-turn history */}
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Per turn</div>
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-slate-400">
                  <th className="text-left py-0.5">#</th>
                  <th className="text-right">Rounds</th>
                  <th className="text-right">Time</th>
                  <th className="text-right">Billed in</th>
                  <th className="text-right">Out</th>
                  <th className="text-right">Cache</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Errs</th>
                </tr>
              </thead>
              <tbody>
                {history.map((t, i) => (
                  <tr key={i} className="text-slate-500 border-t border-slate-50">
                    <td className="py-0.5 tabular-nums">{i + 1}</td>
                    <td className="text-right tabular-nums">{t.rounds}</td>
                    <td className="text-right tabular-nums">{t.elapsed_s}s</td>
                    <td className="text-right tabular-nums">{t.input_tokens?.toLocaleString()}</td>
                    <td className="text-right tabular-nums">{t.output_tokens?.toLocaleString()}</td>
                    <td className="text-right tabular-nums">{t.cache_hit_pct}%</td>
                    <td className="text-right tabular-nums">{fmtCost(computeCost(t))}</td>
                    <td className="text-right tabular-nums">{t.total_errors}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="text-slate-700 font-medium border-t border-slate-200">
                  <td className="py-0.5">Total</td>
                  <td className="text-right tabular-nums">{totals.rounds}</td>
                  <td className="text-right tabular-nums">{Math.round(totals.elapsed)}s</td>
                  <td className="text-right tabular-nums">{totals.input.toLocaleString()}</td>
                  <td className="text-right tabular-nums">{totals.output.toLocaleString()}</td>
                  <td className="text-right tabular-nums">
                    {totals.cacheRead > 0
                      ? Math.round((totals.cacheRead / (totals.input + totals.cacheRead)) * 100)
                      : 0}
                    %
                  </td>
                  <td className="text-right tabular-nums">{fmtCost(totals.cost)}</td>
                  <td className="text-right tabular-nums">{totals.errors}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

DebugFooter.propTypes = { history: PropTypes.array };

// ---------------------------------------------------------------------------
// Main route
// ---------------------------------------------------------------------------

function AnalystRoute() {
  const features = useSettingsStore(store => store.features);
  const {
    messages,
    isStreaming,
    pendingActions,
    projectId,
    projectName,
    workspaceStatus,
    workspaceReview,
    workspaceHistory,
    streamingText,
    debugStats,
    debugStatsHistory,
    studyMode,
    isLoadingConversation,
  } = useAnalystStore();

  // Pre-flight: detect a stale project on a restored conversation so we can
  // offer the user a choice (continue / start fresh) before they type. Without this
  // they only learn the project was deleted after committing to a question
  // and seeing the in-chat replacement notice. Tracked per projectId so a
  // stale check doesn't repeat or flicker.
  const [staleProjectAlert, setStaleProjectAlert] = useState(null);
  const lastCheckedProjectIdRef = useRef(null);

  // Reset the alert when the conversation is wiped, then run the status check
  // exactly once per restored conversation. Without the reset
  // half, the banner sticks on the empty landing after Back, and a re-load
  // of the same recent session won't re-trigger the check.
  useEffect(() => {
    if (!projectId || messages.length === 0) {
      lastCheckedProjectIdRef.current = null;
      // Legitimate reset of derived UI state when the underlying
      // conversation is gone (Back / Start fresh / clearConversation).
      setStaleProjectAlert(null); // eslint-disable-line react-hooks/set-state-in-effect
      return;
    }
    if (isStreaming) return;
    if (lastCheckedProjectIdRef.current === projectId) return;
    lastCheckedProjectIdRef.current = projectId;

    let cancelled = false;
    (async () => {
      try {
        const status = await fetchProjectStatus(projectId);
        if (cancelled) return;
        if (!status.accessible) {
          setStaleProjectAlert({
            previousProjectName: status.name || projectName || null,
          });
        }
      } catch {
        // Network or auth failure — leave the user alone; the next message
        // send will surface any real issue via the existing replacement flow.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, messages.length, isStreaming, projectName]);

  const { sessions, isLoading: isLoadingHistory } = useChatHistoryStore();

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const [workspaceOpen, setWorkspaceOpen] = useState(true);
  const workspaceVisible = workspaceStatus !== 'empty' && workspaceOpen;

  const [splitPct, setSplitPct] = useState(38);
  // isDraggingActive as React state so the transition class can be toggled.
  // isDragging as a ref to avoid stale closure in the event handlers.
  const [isDraggingActive, setIsDraggingActive] = useState(false);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleDividerMouseDown = useCallback(() => {
    isDragging.current = true;
    setIsDraggingActive(true);
  }, []);

  useEffect(() => {
    const onMouseMove = e => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.max(26, Math.min(52, pct)));
    };
    const onMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        setIsDraggingActive(false);
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const chatMessages = messages.filter(
    msg =>
      (msg.role === 'user' ||
        msg.role === 'assistant' ||
        msg.role === 'result' ||
        msg.role === 'thinking' ||
        msg.role === 'system') &&
      !msg.intermediate &&
      !msg.hiddenFromUI,
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages.length, debugStats, isStreaming]);

  const autoResize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 160;
    const clamped = Math.min(el.scrollHeight, max);
    el.style.height = `${clamped}px`;
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  };

  const handleSubmit = e => {
    e?.preventDefault();
    const value = inputRef.current?.value?.trim();
    if (!value || isStreaming || isLoadingConversation) return;
    inputRef.current.value = '';
    autoResize();
    analystActions.sendMessage({ message: value });
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSelectPrompt = prompt => {
    if (inputRef.current) inputRef.current.value = prompt;
    inputRef.current?.focus();
  };

  const handleLoadSession = async conversationId => {
    const loaded = await analystActions.loadConversation(conversationId);
    if (!loaded) toast.error('Could not load conversation');
  };

  const handleWorkspaceToggle = () => {
    setWorkspaceOpen(open => !open);
  };

  // Show thinking dots when the LLM is generating before any tools are queued.
  const hasPendingWork = pendingActions.length > 0;
  const showThinkingDots = isStreaming && !hasPendingWork && !streamingText;
  // Live streaming text bubble — only shown once propose_analysis has fired,
  // so intermediate narration between tool rounds stays hidden.
  const showStreamingText = !!workspaceReview && !!streamingText;
  // Progress spinner — hidden while live text is rendering (it replaces it).
  const showProgress = isStreaming && hasPendingWork && !showStreamingText;
  // Inline "Run analysis" prompt — shown after setup phase completes.
  const showReviewPrompt = !!workspaceReview && workspaceHistory.length === 0 && !isStreaming;

  const chatWidth = workspaceVisible ? `${splitPct}%` : '100%';
  const wsWidth = workspaceVisible ? `${100 - splitPct}%` : '0%';
  // Transition only for collapse/expand — disabled during drag to prevent lag.
  const panelTransition = isDraggingActive ? '' : 'transition-[width] duration-300 ease-in-out';

  if (features && !features.agent_analyst) {
    return <Navigate to="/" replace />;
  }

  const chatContent = (
    <>
      {chatMessages.map((msg, i) => (
        <React.Fragment key={i}>
          {msg.role === 'thinking' ? (
            <ThinkingBubble content={msg.content} />
          ) : msg.role === 'system' ? (
            <SystemNotice content={msg.content} />
          ) : (
            <>
              <MessageBubble message={msg} onNavigate={navigate} />
              {msg.debugStats && <DebugTurnTag stats={msg.debugStats} />}
            </>
          )}
        </React.Fragment>
      ))}
      {showThinkingDots && (
        <div className="py-0.5">
          <span className="loading loading-dots loading-xs text-slate-300" />
        </div>
      )}
      {showProgress && <PhaseIndicator pendingActions={pendingActions} />}
      {showStreamingText && (
        <MessageBubble message={{ role: 'assistant', content: streamingText }} onNavigate={navigate} />
      )}
      {showReviewPrompt && (
        <div className="flex items-center gap-3 py-2">
          <button
            className="px-3 py-1.5 text-xs font-medium bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors"
            onClick={() => analystActions.sendMessage({ message: 'Run analysis' })}
          >
            Run analysis
          </button>
          <span className="text-xs text-slate-400">or ask me to change assumptions first</span>
        </div>
      )}
      <div ref={messagesEndRef} />
    </>
  );

  return (
    <div className="flex flex-col bg-white shadow-xl shadow-slate-200/60 border border-stone-300/60 rounded-lg h-[98%] w-11/12 overflow-hidden">
      <PageTitle className="!p-2 !px-4 !text-sm !font-medium bg-slate-800 text-white" />

      <div ref={containerRef} className="flex flex-1 overflow-hidden">
        {/* ── Chat panel ────────────────────────────────────────────────── */}
        <div
          style={{ width: chatWidth }}
          className={`flex flex-col overflow-hidden shrink-0 bg-stone-50 ${panelTransition}`}
        >
          {/* Minimal top nav */}
          <div className="flex items-center justify-between px-4 py-2 shrink-0 border-b border-stone-100">
            <Link
              to="/analyst"
              onClick={messages.length > 0 ? analystActions.clearConversation : undefined}
              className="text-xs text-slate-400 hover:text-slate-700 transition-colors"
            >
              ← Back
            </Link>
            <BetaBadge className="text-slate-400 border-slate-200 mr-auto ml-3" />
            <div className="flex items-center gap-3">
              {isStreaming && (
                <button
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                  onClick={analystActions.stopStreaming}
                >
                  Stop
                </button>
              )}
              {workspaceStatus !== 'empty' && (
                <button
                  className="text-xs text-slate-400 hover:text-slate-700 transition-colors"
                  onClick={handleWorkspaceToggle}
                >
                  {workspaceOpen ? 'Hide workspace' : 'Show workspace'}
                </button>
              )}
              <button
                className={`text-xs px-2 py-0.5 rounded transition-colors border ${
                  studyMode
                    ? 'bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-200'
                    : 'text-slate-400 border-transparent hover:text-slate-700 hover:border-stone-200'
                }`}
                onClick={analystActions.toggleStudyMode}
                title={
                  studyMode
                    ? 'Max mode on — using Opus with extended analysis'
                    : 'Enable Max mode for comprehensive analyses (Opus + extended thinking)'
                }
              >
                {studyMode ? 'Max ✦' : 'Max'}
              </button>
              {messages.length > 0 && (
                <button
                  className="text-slate-400 hover:text-slate-700 transition-colors"
                  onClick={() => {
                    analystActions.exportConversation();
                    toast.success('Conversation exported', { autoClose: 1500 });
                  }}
                  title="Export conversation as PDF / HTML"
                  aria-label="Export conversation"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Messages — relative container so gradient overlays the scroll area */}
          {workspaceVisible ? (
            <div className="flex-1 relative min-h-0">
              <div className="absolute inset-0 overflow-y-auto">
                <div className="px-5 py-4 space-y-3">
                  {chatContent}
                  {/* Bottom pad so last message clears the input */}
                  <div className="h-20" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-stone-50 to-transparent pointer-events-none" />
            </div>
          ) : (
            <div className="flex-1 relative min-h-0">
              <div className="absolute inset-0 overflow-y-auto">
                {chatMessages.length === 0 ? (
                  <ChatEmptyState
                    onSelectPrompt={handleSelectPrompt}
                    onLoadSession={handleLoadSession}
                    sessions={sessions}
                    isLoading={isLoadingHistory || isLoadingConversation}
                  />
                ) : (
                  <div className="flex flex-col items-center px-6 py-4">
                    <div className="w-full max-w-xl space-y-3">
                      {chatContent}
                      <div className="h-20" />
                    </div>
                  </div>
                )}
              </div>
              {chatMessages.length > 0 && (
                <div className="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-stone-50 to-transparent pointer-events-none" />
              )}
            </div>
          )}

          {/* Stale-project banner — shown on entry when the persisted projectId
              points at a deleted/inaccessible project. Disables the input until
              the user picks a path. */}
          {staleProjectAlert && (
            <StaleProjectBanner
              previousProjectName={staleProjectAlert.previousProjectName}
              onContinue={() => setStaleProjectAlert(null)}
              onStartFresh={() => {
                analystActions.clearConversation();
                setStaleProjectAlert(null);
              }}
            />
          )}

          {/* Input — white container with send button inside */}
          {workspaceVisible ? (
            <form onSubmit={handleSubmit} className="shrink-0 px-3 pb-3 pt-1">
              <div className="flex items-end bg-white border border-stone-200 rounded-xl px-3 py-2 shadow-sm">
                <textarea
                  ref={inputRef}
                  rows={2}
                  className="flex-1 bg-transparent outline-none resize-none text-sm leading-snug placeholder:text-slate-400 overflow-hidden"
                  placeholder={
                    staleProjectAlert ? 'Choose how to continue above…' : isStreaming ? 'Thinking…' : 'Ask a question…'
                  }
                  disabled={isStreaming || isLoadingConversation || !!staleProjectAlert}
                  onKeyDown={handleKeyDown}
                  onInput={autoResize}
                />
                <button
                  type="submit"
                  className="ml-2 p-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-slate-800 transition-colors shrink-0"
                  disabled={isStreaming || isLoadingConversation || !!staleProjectAlert}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path
                      fillRule="evenodd"
                      d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5 px-1">Enter to send · Shift+Enter for new line</p>
            </form>
          ) : (
            <div className="shrink-0 flex justify-center px-4 pb-3 pt-1">
              <form onSubmit={handleSubmit} className="w-full max-w-2xl">
                <div className="flex items-end bg-white border border-stone-200 rounded-xl px-3 py-2 shadow-sm">
                  <textarea
                    ref={inputRef}
                    rows={2}
                    className="flex-1 bg-transparent outline-none resize-none text-sm leading-snug placeholder:text-slate-400 overflow-hidden"
                    placeholder={
                      staleProjectAlert
                        ? 'Choose how to continue above…'
                        : isStreaming
                          ? 'Thinking…'
                          : 'Ask a question…'
                    }
                    disabled={isStreaming || isLoadingConversation || !!staleProjectAlert}
                    onKeyDown={handleKeyDown}
                    onInput={autoResize}
                  />
                  <button
                    type="submit"
                    className="ml-2 p-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-slate-800 transition-colors shrink-0"
                    disabled={isStreaming || isLoadingConversation || !!staleProjectAlert}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path
                        fillRule="evenodd"
                        d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 px-1">Enter to send · Shift+Enter for new line</p>
              </form>
            </div>
          )}
        </div>

        {/* ── Draggable divider ─────────────────────────────────────────── */}
        {workspaceVisible && (
          <div
            className="w-px cursor-col-resize bg-stone-200 hover:bg-stone-300 transition-colors shrink-0 relative group"
            onMouseDown={handleDividerMouseDown}
          >
            <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-0.5 h-0.5 rounded-full bg-slate-400" />
              <div className="w-0.5 h-0.5 rounded-full bg-slate-400" />
              <div className="w-0.5 h-0.5 rounded-full bg-slate-400" />
            </div>
          </div>
        )}

        {/* ── Workspace panel ───────────────────────────────────────────── */}
        <div
          style={{ width: wsWidth }}
          className={`overflow-hidden shrink-0 border-l border-stone-100 ${panelTransition}`}
        >
          <div className="h-full w-full bg-stone-400">
            <WorkspacePanel />
          </div>
        </div>
      </div>
      <DebugFooter history={debugStatsHistory} />
    </div>
  );
}

export default AnalystRoute;
